import type { Carer, Home } from '@lmc/ts-common'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import useZones from 'hooks/useZones'
import _ from 'lodash'
import type React from 'react'
import { useEffect, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Actions from '../../../../actions'
import { Spinner } from '../../../../components/generic'
import Operations from '../../../../operations'
import { history } from '../../../../store'
import { TeamListHeader, TeamListItem, TeamSideFilters } from './components'
import useCarers from './hooks/useCarers'
import { matchesSearch } from './utils'

const StyledRowWrapper = styled.div`
  display: flex;
  margin-top: 0px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    max-height: max-content;
    overflow-y: auto;
    overflow-x: hidden;
  }
`

const StyledMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  max-width: 2000px;
  height: calc(100vh - 109px);
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    height: calc(100vh - 63px);
  }
`

const StyledUserInfoContainer = styled.div`
  padding-top: 54px;
  margin: 0 24px;
  height: 100%;
`

const StyledScrollerDesktop = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 210px);
  overflow-y: auto;
  width: 100%;
`

const TeamListLegacy: React.FC = () => {
  const dispatch = useDispatch()

  const [usersFetchInterval, setUsersFetchInterval] = useState<
    ReturnType<typeof setInterval> | undefined
  >()

  const [search, setSearch] = useState<string>('')

  const [roles, setRoles] = useState<string[]>([])

  const [statuses, setStatuses] = useState<string[]>(['active'])

  const { isLoading, carers, fetchCarers } = useCarers()

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const hasRolePermission = useAbilityAccess([
    {
      i: 'create',
      this: 'UserInvitation'
    }
  ])

  const { fetchZones } = useZones()

  const { t } = useTranslation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (features?.pro) {
      fetchZones()
    }
    setUsersFetchInterval(setInterval(fetchCarers, 600000))
    fetchCarers()
    return () => {
      clearInterval(usersFetchInterval)
    }
  }, [])

  const openModal = () => {
    const id = 'addTeamMember'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  }

  const hasCarers = !!carers?.length

  const searchResults = _.filter(carers, (carer) =>
    matchesSearch(carer, roles, statuses, search)
  ) as Carer[]

  if (isLoading) return <Spinner />

  const hasSearchResults = !!searchResults?.length

  const teamList = hasSearchResults ? (
    searchResults.map((carer: Carer, index) => (
      <TeamListItem
        testId="team-list-item"
        firstItem={index === 0}
        index={index}
        key={carer.id}
        carer={carer}
        onClick={(e) => {
          e.stopPropagation()
          history.push(`/organisation/team/${carer.id}/profile`)
        }}
      />
    ))
  ) : (
    <EmptyState
      title={t(`general.emptyState.noTeamListResultTitle`)}
      description={t(`general.emptyState.noTeamListResultDescription`)}
      icon={<NoDataIcon />}
      testId="team-list-no-results"
    />
  )

  const handleSubscribePro = () => {
    dispatch(Operations.openProPlanCheckout({}))
  }

  return (
    <StyledMainContainer data-testid="team-list">
      <TeamListHeader
        testId="team-list"
        openModal={openModal}
        onSearch={setSearch}
        title={t(`general.team.teamMembers`)}
        searchPlaceholder={t(`general.team.typeToSearch`)}
        buttonTitle={t(`general.team.inviteTeamMember`)}
        buttonDisabled={!hasRolePermission}
      />
      {hasCarers ? (
        <StyledRowWrapper data-testid="team-list-content">
          <TeamSideFilters
            carers={carers}
            onRoles={setRoles}
            onStatuses={setStatuses}
            statuses={statuses}
            roles={roles}
            features={features}
            clearButtonTitle={t(`general.buttons.clear`)}
            statusTitle={t(`general.team.status`)}
            roleTitle={t(`general.team.role`)}
            viewProTitle={t(`general.team.viewPro`)}
            onSubscribe={handleSubscribePro}
            onDefaultOpened={isDesktop}
            testId="team-side-filters"
          />
          {isDesktop ? (
            <StyledScrollerDesktop data-testid="team-list-scroller">
              {teamList}
            </StyledScrollerDesktop>
          ) : (
            <div data-testid="team-list-mobile">{teamList}</div>
          )}
        </StyledRowWrapper>
      ) : (
        <StyledUserInfoContainer data-testid="team-list-empty">
          <EmptyState
            title={t(`general.emptyState.noTeamListResponseTitle`)}
            description={t(`general.emptyState.noTeamListResponseDescription`)}
            icon={<NoDataIcon />}
            testId="team-list-empty-state"
          />
        </StyledUserInfoContainer>
      )}
    </StyledMainContainer>
  )
}

const isDesktop = isBrowser || isTablet

export default TeamListLegacy
